import { useState } from 'react';
import type { StorageMode } from '../../store/types.ts';
import { STARTER_TEMPLATES } from '../../store/types.ts';

type OnboardingFlowProps = {
  open: boolean;
  keyStorageMode: StorageMode;
  apiKey: string;
  showApiKey: boolean;
  onUpdateKeyStorageMode: (mode: StorageMode) => void;
  onUpdateApiKey: (key: string) => void;
  onToggleShowKey: () => void;
  onFinish: (templateId: string) => void;
  onSkip: () => void;
};

export function OnboardingFlow({
  open,
  keyStorageMode,
  apiKey,
  showApiKey,
  onUpdateKeyStorageMode,
  onUpdateApiKey,
  onToggleShowKey,
  onFinish,
  onSkip,
}: OnboardingFlowProps) {
  const [step, setStep] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState(STARTER_TEMPLATES[0].id);

  if (!open) return null;

  return (
    <div className="ds-modal-overlay" role="dialog" aria-modal="true" aria-label="Welcome">
      <div className="ds-modal">
        <div className="ds-modal__head">
          <h3 className="ds-modal__title">Welcome to Delegate Studio</h3>
          <button type="button" className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onSkip}>
            Skip
          </button>
        </div>

        <div className="ds-onboarding__steps">
          {[0, 1, 2].map((s) => (
            <div key={s} className={`ds-onboarding__step ${step === s ? 'ds-onboarding__step--active' : ''}`}>
              {s + 1}
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="ds-onboarding__body">
            <h4>Describe it. Delegate it. Done.</h4>
            <p>Generate diagrams with natural language, edit Mermaid directly, or draw freely on a whiteboard.</p>
            <ul>
              <li>Chat + code workflows side-by-side</li>
              <li>Auto syntax validation with optional auto-fix</li>
              <li>Full revision history with restore and undo/redo</li>
              <li>Excalidraw whiteboard for freeform drawing</li>
            </ul>
          </div>
        )}

        {step === 1 && (
          <div className="ds-onboarding__body">
            <h4>Configure your API key</h4>
            <p>Choose how key persistence works for your risk profile.</p>
            <fieldset className="ds-fieldset">
              <legend>Storage mode</legend>
              <label className="ds-radio">
                <input
                  type="radio"
                  checked={keyStorageMode === 'session'}
                  onChange={() => onUpdateKeyStorageMode('session')}
                />
                <span className="ds-radio__text">
                  Session only
                  <small className="ds-radio__hint">Recommended for shared machines.</small>
                </span>
              </label>
              <label className="ds-radio">
                <input
                  type="radio"
                  checked={keyStorageMode === 'local'}
                  onChange={() => onUpdateKeyStorageMode('local')}
                />
                <span className="ds-radio__text">
                  Remember on this device
                  <small className="ds-radio__hint">Best for personal laptops.</small>
                </span>
              </label>
            </fieldset>
            <div className="ds-field" style={{ marginTop: 12 }}>
              <span className="ds-field__label">Anthropic API key</span>
              <div className="ds-api-row">
                <input
                  className="ds-field__input"
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => onUpdateApiKey(e.target.value)}
                  placeholder="sk-ant-..."
                />
                <button type="button" className="ds-btn ds-btn--sm" onClick={onToggleShowKey}>
                  {showApiKey ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="ds-onboarding__body">
            <h4>Pick a starter prompt</h4>
            <p>We'll drop this into the prompt box so you can generate immediately.</p>
            <div className="ds-template-grid">
              {STARTER_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className={`ds-template-btn ${template.id === selectedTemplateId ? 'ds-template-btn--active' : ''}`}
                  onClick={() => setSelectedTemplateId(template.id)}
                >
                  <strong>{template.label}</strong>
                  <span>{template.prompt}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="ds-modal__actions">
          <button
            type="button"
            className="ds-btn ds-btn--ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            Back
          </button>
          {step < 2 ? (
            <button type="button" className="ds-btn ds-btn--primary" onClick={() => setStep((s) => Math.min(2, s + 1))}>
              Next
            </button>
          ) : (
            <button type="button" className="ds-btn ds-btn--primary" onClick={() => onFinish(selectedTemplateId)}>
              Finish
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
